/**
 * Mobile header menu — drill-down model.
 *
 * Levels:
 *   L1  Top-level primary nav (slides down from top with the panel itself).
 *   L2  Depth-1 children of the active L1 item (slides in from the right;
 *       covers L1, has a "Back to main menu" button to return).
 *   L3  Depth-2 children of an L2 item (inline accordion; only one open at
 *       a time within the active L2 screen).
 *
 * Behaviour:
 *   - Hamburger toggles `body.mobile-menu-open` → panel slides down (CSS).
 *   - Tapping an L1 parent with children sets `is-active` on that <li>
 *     and adds `mobile-submenu-open` to <body> → its <.sub-menu> slides
 *     in from the right and the back button appears.
 *   - Back button removes both classes → return to L1.
 *   - Inside the L2 screen, tapping an L3 parent toggles `is-expanded` on
 *     its <li>; siblings with `is-expanded` are cleared first.
 *   - Tapping a leaf link closes the whole panel.
 *   - Resizing back to desktop tears the panel down cleanly.
 */

import { getLenisInstance } from '../../core/scroll-manager';

const MOBILE_BREAKPOINT = 640;

export function initializeMobileMenu() {
    const header = document.getElementById('header');
    if (!header) return;

    const panel = header.querySelector('.header__menu-panel') || header.querySelector('.header__nav');
    const toggles = header.querySelectorAll('.header__menu-toggle');
    const backBtn = header.querySelector('.header__back');
    if (!panel || !toggles.length) return;

    const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

    const closeAllAccordions = () => {
        panel.querySelectorAll('li.is-expanded').forEach(li => li.classList.remove('is-expanded'));
    };

    const closeSubmenu = () => {
        document.body.classList.remove('mobile-submenu-open');
        panel.querySelectorAll('.header__primary > ul > li.is-active')
            .forEach(li => li.classList.remove('is-active'));
        closeAllAccordions();
        if (backBtn) backBtn.hidden = true;
    };

    const closePanel = () => {
        document.body.classList.remove('mobile-menu-open');
        toggles.forEach(t => {
            t.setAttribute('aria-expanded', 'false');
            t.classList.remove('is-active');
        });
        closeSubmenu();
        const lenis = getLenisInstance();
        if (lenis) lenis.start();
    };

    const openPanel = () => {
        if (!isMobile()) return;
        document.body.classList.add('mobile-menu-open');
        toggles.forEach(t => {
            t.setAttribute('aria-expanded', 'true');
            t.classList.add('is-active');
        });
        const lenis = getLenisInstance();
        if (lenis) lenis.stop();
    };

    toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            if (document.body.classList.contains('mobile-menu-open')) {
                closePanel();
            } else {
                openPanel();
            }
        });
    });

    // L1 → L2 drill-down: top-level parents only
    const l1Triggers = panel.querySelectorAll(
        '.header__primary > ul > li.menu-item-has-children > a'
    );
    l1Triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            if (!isMobile()) return;
            e.preventDefault();
            e.stopPropagation();

            const parentLi = trigger.parentElement;
            const wasActive = parentLi.classList.contains('is-active');

            // Clear any other top-level active state and accordions before activating this one.
            panel.querySelectorAll('.header__primary > ul > li.is-active')
                .forEach(li => li.classList.remove('is-active'));
            closeAllAccordions();

            if (!wasActive) {
                parentLi.classList.add('is-active');
                document.body.classList.add('mobile-submenu-open');
                if (backBtn) backBtn.hidden = false;
                // Reset scroll so the new screen starts at the top.
                panel.scrollTop = 0;
            } else {
                document.body.classList.remove('mobile-submenu-open');
                if (backBtn) backBtn.hidden = true;
            }
        });
    });

    // L2 → L3 accordion: depth-1 parents inside .sub-menu__nav
    const l3Triggers = panel.querySelectorAll(
        '.sub-menu__nav > ul > li.menu-item-has-children > a'
    );
    l3Triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            if (!isMobile()) return;
            e.preventDefault();
            e.stopPropagation();

            const li = trigger.parentElement;
            const wasOpen = li.classList.contains('is-expanded');
            // Only one accordion open at a time within this L2 screen.
            const sibList = li.parentElement;
            sibList.querySelectorAll(':scope > li.is-expanded').forEach(s => {
                if (s !== li) s.classList.remove('is-expanded');
            });
            li.classList.toggle('is-expanded', !wasOpen);
            trigger.setAttribute('aria-expanded', !wasOpen ? 'true' : 'false');
        });
    });

    // Back button: leave L2, return to L1.
    if (backBtn) {
        backBtn.addEventListener('click', (e) => {
            e.preventDefault();
            closeSubmenu();
        });
    }

    // Leaf links close the whole panel.
    panel.querySelectorAll('li:not(.menu-item-has-children) > a').forEach(link => {
        link.addEventListener('click', () => {
            if (isMobile()) closePanel();
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (document.body.classList.contains('mobile-submenu-open')) {
            closeSubmenu();
        } else if (document.body.classList.contains('mobile-menu-open')) {
            closePanel();
        }
    });

    document.addEventListener('barba-leave-start', closePanel);

    window.addEventListener('resize', () => {
        if (!isMobile() && document.body.classList.contains('mobile-menu-open')) {
            closePanel();
        }
    });
}
